import { Client } from "memjs";
import { env } from "./env";

/**
 * Memcached client for hot reads — current-company settings, a user's effective permissions,
 * navigation (reskin-docs/01 §6, 07 §7). Cache MUST be invalidated when the underlying
 * setting/permission changes; helpers for that live alongside each feature.
 *
 * RESILIENT BY DESIGN: a cache outage must never break a request. Every helper swallows errors
 * — a miss/unreachable Memcached just degrades to "no cache" (recompute), never a 500.
 */
export const cache = Client.create(env.MEMCACHED_SERVERS, {
  username: env.MEMCACHED_USERNAME || undefined,
  password: env.MEMCACHED_PASSWORD || undefined,
});

/** Get + JSON-parse a cached value, or null on miss / any cache error. */
export async function cacheGetJSON<T>(key: string): Promise<T | null> {
  try {
    const { value } = await cache.get(key);
    return value ? (JSON.parse(value.toString()) as T) : null;
  } catch {
    return null; // Memcached down → treat as miss
  }
}

/** Set a JSON value with an optional TTL (seconds). No-op on cache error. */
export async function cacheSetJSON(key: string, value: unknown, expiresSeconds = 300): Promise<void> {
  try {
    await cache.set(key, JSON.stringify(value), { expires: expiresSeconds });
  } catch {
    /* cache write best-effort */
  }
}

/** Delete a key. No-op on cache error. */
export async function cacheDelete(key: string): Promise<void> {
  try {
    await cache.delete(key);
  } catch {
    /* best-effort */
  }
}
